import { useCallback, useEffect, useState } from "react";

export type Consent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
};

const COOKIE_NAME = "tcs_cookie_consent_v1";
const COOKIE_MAX_AGE_DAYS = 365;

const defaultConsent = (): Consent => ({ necessary: true, analytics: false, marketing: false });

function getCookie(name: string, cookieString?: string): string | null {
  const src = cookieString ?? (typeof document !== "undefined" ? document.cookie : "");
  if (!src) return null;
  const match = src.split("; ").find((c) => c.startsWith(name + "="));
  if (!match) return null;
  return decodeURIComponent(match.split("=").slice(1).join("="));
}

function setCookie(name: string, value: string, days = COOKIE_MAX_AGE_DAYS) {
  if (typeof document === "undefined") return;
  const maxAge = days * 24 * 60 * 60;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  const sameSite = "; SameSite=Lax";
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}${sameSite}${secure}`;
}

export function parseConsentCookie(cookieString?: string): Consent | null {
  try {
    const raw = getCookie(COOKIE_NAME, cookieString);
    return raw ? (JSON.parse(raw) as Consent) : null;
  } catch {
    return null;
  }
}

export default function useCookieConsent() {
  const [consent, setConsent] = useState<Consent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = parseConsentCookie();
    setConsent(stored ?? null);
  }, []);

  const save = useCallback((next: Consent) => {
    try {
      setCookie(COOKIE_NAME, JSON.stringify(next));
      setConsent(next);
    } catch (e) {
      console.warn("Failed to save cookie consent", e);
    }
  }, []);

  const acceptAll = useCallback(() => save({ necessary: true, analytics: true, marketing: true }), [save]);
  const rejectNonEssential = useCallback(() => save(defaultConsent()), [save]);

  return {
    consent,
    save,
    acceptAll,
    rejectNonEssential,
    defaultConsent,
    parseConsentCookie,
  } as const;
}
